import { CalculationConfig } from '@calculations/types/calculation-config';
import { marketPriceToPriceBook } from '@snap-types/models/price-book';
import type { VisualMarketPrice } from '@snap-types/models/visual/market-price';
import type { Decimal } from '@snap-types/types/decimal';

import { convertPriceBookToQuantityBreaks } from '../../utilities/quantity-breaks';

export type FixedMarketCalculateParams = {
  quantity: Decimal;
  marketCode: string;
  marketPrices: VisualMarketPrice[];
};

export type FixedMarketCalculateResult = {
  quantity: Decimal;
  marketCode: string;
  unitPrice: Decimal;
  marketPrice: VisualMarketPrice;
};

export function calculate(
  {
    quantity,

    marketCode,
    marketPrices,
  }: FixedMarketCalculateParams,
  config: CalculationConfig,
): FixedMarketCalculateResult | null {
  const marketPrice = marketPrices.find(
    (price) => price.market_id === marketCode,
  );

  // No entry in price book for the given code
  if (!marketPrice) {
    return null;
  }

  const quantityBreaks = convertPriceBookToQuantityBreaks(
    marketPriceToPriceBook(marketPrice),
  );

  for (let i = 0; i < quantityBreaks.length; i++) {
    const lowBreak = quantityBreaks[i];

    // Check for the last quantity break
    if (i === quantityBreaks.length - 1) {
      if (quantity.gte(lowBreak.quantity)) {
        return {
          quantity,
          marketCode,
          marketPrice,
          unitPrice: lowBreak.unitPrice.toDecimalPlaces(config.decimalPlaces),
        };
      }

      return null;
    }

    // We're not at the last quantity break, so get the next one
    // and see if the requested quantity is between them.
    const highBreak = quantityBreaks[i + 1];

    if (quantity.gte(lowBreak.quantity) && quantity.lt(highBreak.quantity)) {
      return {
        quantity,
        marketCode,
        marketPrice,
        unitPrice: lowBreak.unitPrice.toDecimalPlaces(config.decimalPlaces),
      };
    }
  }

  return null;
}
